import React, {
  createContext,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { chatReadyEvent } from '@guuru/chat-events-web';

interface ExternalNavigationProviderProps {
  children: React.ReactNode;
}

type WaitingSignal = boolean | string | null;

const ExternalNavigationContext = createContext<WaitingSignal>(null);

export const ExternalNavigationProvider = function ({
  children,
}: ExternalNavigationProviderProps) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const [waitingSignal, setWaitingSignal] = useState<WaitingSignal>(
    params.get('waitSignal'),
  );
  const replace = useRef(false);

  useEffect(() => {
    chatReadyEvent();
  }, []);

  const handleWidgetMessage = useEffectEvent((event: MessageEvent) => {
    const { type, path, options } = event.data;

    if (type === 'navigate' && path) {
      setWaitingSignal(false);
      navigate(path, {
        ...options,
        state: { hideBack: true, external: true },
        replace: replace.current,
      });
      replace.current = true;
    }

    if (replace.current && type === 'widgetClosed') {
      navigate(-1);
      replace.current = false;
    }
  });

  useEffect(() => {
    const handleBackAndForward = ({ state }: PopStateEvent) => {
      const { usr } = state || {};
      replace.current = !!usr?.external;
    };

    window.addEventListener('message', handleWidgetMessage);
    window.addEventListener('popstate', handleBackAndForward);

    return () => {
      window.removeEventListener('message', handleWidgetMessage);
      window.removeEventListener('popstate', handleBackAndForward);
    };
  }, []);

  return (
    <ExternalNavigationContext value={waitingSignal}>
      {children}
    </ExternalNavigationContext>
  );
};

export default ExternalNavigationContext;
